/**
 * Workspace Marketplace Sources API Client
 * Manages custom and built-in marketplace sources per workspace.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MarketplaceSourceConfig } from './plugins';

const API_BASE = '/api/workspaces';

export function useMarketplaceSources(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['marketplace-sources', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const res = await fetch(`${API_BASE}/${workspaceId}/marketplace-sources`);
      if (!res.ok) throw new Error(`Failed to fetch marketplace sources: ${res.statusText}`);
      return (await res.json()) as MarketplaceSourceConfig[];
    },
    enabled: !!workspaceId,
  });
}

export function useUpdateMarketplaceSources(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sources: MarketplaceSourceConfig[]) => {
      const res = await fetch(`${API_BASE}/${workspaceId}/marketplace-sources`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sources }),
      });
      if (!res.ok) throw new Error(`Failed to update marketplace sources: ${res.statusText}`);
      return (await res.json()) as MarketplaceSourceConfig[];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketplace-sources', workspaceId] });
      qc.invalidateQueries({ queryKey: ['marketplace-plugins', workspaceId] });
    },
  });
}
